// frontend/src/constants/crsFormOptions.js

// Import the official points data to generate options
// Note: This assumes officialCrsCalculations.js is correctly located relative to this constants file.
// Adjust the path if necessary.
import { languageTestPoints as officialLanguageTestPoints } from '../utils/officialCrsCalculations';

export const BAD_VALUE = "badvalue"; // Represents the default "Select..." option

export const maritalStatusOptions = [
  { value: BAD_VALUE, label: "Select..." },
  { value: "A", label: "Annulled Marriage" },
  { value: "B", label: "Common-Law" },
  { value: "C", label: "Divorced / Separated" },
  { value: "D", label: "Legally Separated" },
  { value: "E", label: "Married" },
  { value: "F", label: "Never Married / Single" },
  { value: "G", label: "Widowed" },
];

export const yesNoOptions = [
  { value: BAD_VALUE, label: "Select..." },
  { value: "A", label: "No" }, // Corresponds to 'No' in original script logic
  { value: "B", label: "Yes" }, // Corresponds to 'Yes' in original script logic
];

export const ageOptions = [
  { value: BAD_VALUE, label: "Select..." },
  { value: "A", label: "17 years of age or less" },
  { value: "B", label: "18 years of age" },
  { value: "C", label: "19 years of age" },
  { value: "D", label: "20 years of age" },
  { value: "E", label: "21 years of age" },
  { value: "F", label: "22 years of age" },
  { value: "G", label: "23 years of age" },
  { value: "H", label: "24 years of age" },
  { value: "I", label: "25 years of age" },
  { value: "J", label: "26 years of age" },
  { value: "K", label: "27 years of age" },
  { value: "L", label: "28 years of age" },
  { value: "M", label: "29 years of age" },
  { value: "N", label: "30 years of age" },
  { value: "O", label: "31 years of age" },
  { value: "P", label: "32 years of age" },
  { value: "Q", label: "33 years of age" },
  { value: "R", label: "34 years of age" },
  { value: "S", label: "35 years of age" },
  { value: "T", label: "36 years of age" },
  { value: "U", label: "37 years of age" },
  { value: "V", label: "38 years of age" },
  { value: "W", label: "39 years of age" },
  { value: "X", label: "40 years of age" },
  { value: "Y", label: "41 years of age" },
  { value: "Z", label: "42 years of age" },
  { value: "AA", label: "43 years of age" },
  { value: "AB", label: "44 years of age" },
  { value: "AC", label: "45 years of age or more" },
];

export const educationLevelOptions = [
  { value: BAD_VALUE, label: "Select..." },
  { value: "A", label: "None, or less than secondary (high school)" },
  { value: "B", label: "Secondary diploma (high school graduation)" },
  { value: "C", label: "One-year program at a university, college, trade or technical school, or other institute" },
  { value: "D", label: "Two-year program at a university, college, trade or technical school, or other institute" },
  { value: "E", label: "Bachelor's degree (three or more year program at a university, college, trade or technical school, or other institute)" },
  { value: "F", label: "Two or more certificates, diplomas or degrees. One must be for a program of three or more years" },
  { value: "G", label: "Master's degree, or professional degree needed to practice in a licensed profession" },
  { value: "H", label: "Doctoral level university degree (PhD)" },
];

export const canadianEducationLevelOptions = [
   { value: BAD_VALUE, label: "Select..." },
   { value: "A", label: "None, or less than secondary (high school)" }, // Although unlikely if 'Yes' to studied in Canada
   { value: "B", label: "One- or two-year diploma or certificate" },
   { value: "C", label: "Degree, diploma or certificate of three years or longer OR a Master’s, professional or doctoral degree of at least one academic year" },
];

export const languageTestOptions = [
  { value: BAD_VALUE, label: "Select..." },
  { value: "A", label: "CELPIP-G" },
  { value: "B", label: "IELTS" },
  { value: "C", label: "TEF Canada" },
  { value: "D", label: "TCF Canada" },
  { value: "E", label: "PTE Core" },
];

// Map test value ('A', 'B', etc.) to test name ('celpip', 'ielts', etc.)
export const testValueToNameMap = {
  A: 'celpip',
  B: 'ielts',
  C: 'tef',
  D: 'tcf',
  E: 'pte',
};


// Generate language score options directly from the imported data table
export const languageScoreOptions = Object.entries(testValueToNameMap).reduce((acc, [testValue, testName]) => {
  const testData = officialLanguageTestPoints[testName]; // Use the imported data directly
  if (testData) {
    acc[testValue] = {
      speaking: [{ value: BAD_VALUE, label: "Select..." }, ...testData.map(row => ({ value: row[0], label: row[1] }))],
      listening: [{ value: BAD_VALUE, label: "Select..." }, ...testData.map(row => ({ value: row[0], label: row[2] }))],
      reading: [{ value: BAD_VALUE, label: "Select..." }, ...testData.map(row => ({ value: row[0], label: row[3] }))],
      writing: [{ value: BAD_VALUE, label: "Select..." }, ...testData.map(row => ({ value: row[0], label: row[4] }))],
    };
  }
  return acc;
}, {});

export const canadianWorkExperienceOptions = [
  { value: BAD_VALUE, label: "Select..." },
  { value: "A", label: "None or less than a year" },
  { value: "B", label: "1 year" },
  { value: "C", label: "2 years" },
  { value: "D", label: "3 years" },
  { value: "E", label: "4 years" },
  { value: "F", label: "5 years or more" },
];

export const foreignWorkExperienceOptions = [
  { value: BAD_VALUE, label: "Select..." },
  { value: "A", label: "None or less than a year" },
  { value: "B", label: "1 year" }, // Note: Original script groups 1 and 2 years for some calcs
  { value: "C", label: "2 years" },
  { value: "D", label: "3 years or more" },
];

export const jobOfferNocOptions = [
   { value: BAD_VALUE, label: "Select..." },
   { value: "A", label: "NOC TEER 0 Major Group 00" }, // Corresponds to '00' in calculator
   { value: "B", label: "NOC TEER 1, 2 or 3, or any TEER 0 other than Major group 00" }, // Corresponds to '0123'
   // { value: "C", label: "NOC TEER 4 or 5" }, // No points in standard CRS, omit for now
];